import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ENVIRONMENT_LABEL } from "@/components/app-registration-badges";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type AppRegistrationRequestDetail,
  useRequestAppRegistrationDecommission,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

/** The backend's own minimum for the reason (`min_length=10`). */
const MIN_REASON_LENGTH = 10;

interface AppRegistrationDecommissionDialogProps {
  request: AppRegistrationRequestDetail | null;
  projectId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Ask for an app registration to be deleted.
 *
 * This dialog does not delete anything: it raises a decommission *request*
 * that an approver decides, exactly like the request that created the
 * registration in the first place. The wording says so, because a dialog with
 * a red button that reads like it deletes on click gets clicked by people who
 * expected a preview.
 *
 * Two things are made unmissable before the button enables:
 *
 * - **What stops working.** Deleting the application invalidates its client id
 *   immediately for whatever is using it. Scaffold UI never wrote that id into
 *   the project's deployment and cannot clear it, so the deployment keeps
 *   pointing at an application that no longer exists — this is the one
 *   consequence the desk cannot clean up for you.
 * - **Which environment.** One registration serves one environment, and
 *   `NO-AR-Thing-DEV` and `NO-AR-Thing-PRD` differ by three characters. Typing
 *   the full name back is the confirmation, and it is checked server-side too.
 */
export function AppRegistrationDecommissionDialog({
  request,
  projectId,
  open,
  onOpenChange,
}: AppRegistrationDecommissionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {request && open && (
          // Remount per request so a half-typed confirmation can never be
          // submitted against a different registration.
          <DecommissionForm
            key={request.id}
            request={request}
            projectId={projectId}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DecommissionForm({
  request,
  projectId,
  onClose,
}: {
  request: AppRegistrationRequestDetail;
  projectId: number;
  onClose: () => void;
}) {
  const decommission = useRequestAppRegistrationDecommission(projectId);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);

  const pending = decommission.isPending;
  const nameMatches = confirmation.trim() === request.display_name;
  const canSubmit = nameMatches && reason.trim().length >= MIN_REASON_LENGTH && !pending;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setError(null);
    try {
      await decommission.mutateAsync({
        requestId: request.id,
        reason: reason.trim(),
        confirmDisplayName: confirmation.trim(),
      });
      toast.success("Decommission requested — an approver has to carry it out.");
      onClose();
    } catch (err) {
      logger.error("[app-registration] decommission request failed:", err);
      setError(readApiErrorMessage(err, "Couldn't request the decommission. Please try again."));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Decommission this app registration</DialogTitle>
        <DialogDescription>
          <strong className="font-mono">{request.display_name}</strong>
          {request.environment ? ` — ${ENVIRONMENT_LABEL[request.environment]}` : ""}. This raises a
          request; an approver deletes the application from Entra.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <p className="flex items-start gap-2 font-medium">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              Once an approver carries this out, it cannot be undone from here.
            </p>
            <ul className="ml-6 list-disc space-y-1 text-xs">
              <li>
                The application, its enterprise app and its federated credential are deleted, and
                the admin consent granted on it goes with them.
              </li>
              <li>
                Anything still using this client id stops being able to call Microsoft Graph{" "}
                <strong>immediately</strong>.
              </li>
              <li>
                Scaffold UI cannot edit your project&apos;s deployment. If{" "}
                <code className="font-mono">GRAPH_CLIENT_ID</code> is still set there, remove it and
                redeploy &mdash; nothing here will do that for you.
              </li>
              <li>
                {request.environment
                  ? `Only ${ENVIRONMENT_LABEL[request.environment]} is`
                  : "Only this environment is"}{" "}
                affected. This project&apos;s other environments keep their own registrations.
              </li>
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="decommission-reason">Why is it no longer needed?</Label>
            <Textarea
              id="decommission-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. the reporting job was retired; nothing calls Graph any more."
              rows={3}
              disabled={pending}
            />
            <p className="text-muted-foreground text-xs">
              The approver reads this before deleting anything.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="decommission-confirm">
              Type <span className="font-mono">{request.display_name}</span> to confirm
            </Label>
            <Input
              id="decommission-confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={request.display_name}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              disabled={pending}
            />
            {confirmation.length > 0 && !nameMatches && (
              <p className="text-muted-foreground text-xs">
                That is not this registration&apos;s name. Each environment has its own.
              </p>
            )}
          </div>

          {error && (
            <p className="text-destructive flex items-start gap-2 text-sm">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!canSubmit}>
            {pending ? "Requesting…" : "Request decommission"}
          </Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}
